import { useEffect, useState } from "react";
import { Filter, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DEFAULT_FILTERS,
  isDefaultFilters,
  type DiscoveryFilters,
} from "@/lib/discovery/filters";

interface Props {
  filters: DiscoveryFilters;
  onApply: (next: DiscoveryFilters) => void;
  onReset: () => void;
  resultCount: number;
  rawCount: number;
}

const LANE_OPTIONS: { value: DiscoveryFilters["lane"]; label: string }[] = [
  { value: "any", label: "Any lane" },
  { value: "dating", label: "Dating" },
  { value: "friendship", label: "Friendship" },
  { value: "mentorship", label: "Mentorship" },
  { value: "both", label: "Both" },
];

const DiscoveryFiltersSheet = ({
  filters,
  onApply,
  onReset,
  resultCount,
  rawCount,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DiscoveryFilters>(filters);

  // Re-sync the draft when the sheet is reopened so cancelled edits don't leak.
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const activeCount = (() => {
    let n = 0;
    if (filters.maxKm !== DEFAULT_FILTERS.maxKm) n += 1;
    if (filters.lane !== DEFAULT_FILTERS.lane) n += 1;
    if (filters.verifiedOnly) n += 1;
    if (filters.withVoiceIntro) n += 1;
    if (filters.promptKeyword.trim()) n += 1;
    return n;
  })();

  const handleApply = () => {
    onApply(draft);
    setOpen(false);
  };

  const handleResetAll = () => {
    setDraft(DEFAULT_FILTERS);
    onReset();
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full gap-2"
          aria-label="Open discovery filters"
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <Badge variant="secondary" className="rounded-full px-2 py-0 text-[10px]">
              {activeCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Refine Discover</SheetTitle>
          <SheetDescription>
            Narrow today&apos;s queue. Filters save automatically and reapply next
            visit.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="maxKm" className="text-sm font-medium">
                Max distance
              </Label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {draft.maxKm} km
              </span>
            </div>
            <Slider
              id="maxKm"
              min={5}
              max={500}
              step={5}
              value={[draft.maxKm]}
              onValueChange={(v) => setDraft({ ...draft, maxKm: v[0] ?? draft.maxKm })}
            />
            <p className="text-[11px] text-muted-foreground">
              Changing distance reloads candidates from the server.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Friendship lane</Label>
            <Select
              value={draft.lane}
              onValueChange={(v) =>
                setDraft({ ...draft, lane: v as DiscoveryFilters["lane"] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-2xl border border-border p-3">
            <div>
              <Label htmlFor="verifiedOnly" className="text-sm font-medium">
                Verified only
              </Label>
              <p className="text-[11px] text-muted-foreground">
                BGC-verified or community-vouched members.
              </p>
            </div>
            <Switch
              id="verifiedOnly"
              checked={draft.verifiedOnly}
              onCheckedChange={(c) => setDraft({ ...draft, verifiedOnly: c })}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-2xl border border-border p-3">
            <div>
              <Label htmlFor="withVoiceIntro" className="text-sm font-medium">
                Has voice intro
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Members who&apos;ve recorded a hello.
              </p>
            </div>
            <Switch
              id="withVoiceIntro"
              checked={draft.withVoiceIntro}
              onCheckedChange={(c) => setDraft({ ...draft, withVoiceIntro: c })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="promptKeyword" className="text-sm font-medium">
              Prompt keyword
            </Label>
            <Input
              id="promptKeyword"
              placeholder="e.g. trail running, jazz, philosophy"
              value={draft.promptKeyword}
              onChange={(e) =>
                setDraft({ ...draft, promptKeyword: e.target.value })
              }
              maxLength={60}
            />
            <p className="text-[11px] text-muted-foreground">
              Matches against profile prompt answers.
            </p>
          </div>

          <div className="rounded-2xl bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            Showing <span className="font-medium text-foreground">{resultCount}</span>{" "}
            of {rawCount} candidates with current filters.
          </div>
        </div>

        <SheetFooter className="flex-row gap-2 sm:flex-row">
          <Button
            type="button"
            variant="ghost"
            onClick={handleResetAll}
            disabled={isDefaultFilters(filters) && isDefaultFilters(draft)}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          <Button type="button" onClick={handleApply} className="flex-1">
            Apply filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default DiscoveryFiltersSheet;
